import { CartAddEvent } from '@theme/events';

const MAX_ITEMS = 3;
const COLLAPSE_KEY = 'pd:cart-recommendations-collapsed';

/**
 * Renders up to 3 product recommendations for the cart's current items,
 * sourced from Shopify's native recommendation engine (Search & Discovery)
 * via /recommendations/products.json - the same source used by
 * `product-recommendations.js` elsewhere in the theme.
 */
class CartDrawerRecommendations extends HTMLElement {
  static observedAttributes = ['data-targets'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLDialogElement | null} */
  #picker = null;

  connectedCallback() {
    this.#waitForHydration().then(() => this.#load());
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-targets' && oldValue !== newValue) {
      this.#load();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#picker?.remove();
    this.#picker = null;
  }

  /**
   * The parent header section runs a one-time client-side "hydration" pass
   * shortly after load (see snippets/header-actions.liquid's sibling
   * `hydrate()` call in sections/header.liquid), which morphs the section's
   * markup against a fresh server render. That morph has no knowledge of
   * cards we've already injected client-side and wipes them out. Waiting
   * for that one-time pass to finish (it's guarded by `data-hydrated` and
   * never repeats) avoids the race instead of fighting it.
   */
  async #waitForHydration() {
    const section = this.closest('.shopify-section');
    if (!section || section.dataset.hydrated === 'true') return;

    await new Promise((resolve) => {
      const timeout = setTimeout(() => {
        observer.disconnect();
        resolve(undefined);
      }, 4000);

      const observer = new MutationObserver(() => {
        if (section.dataset.hydrated === 'true') {
          clearTimeout(timeout);
          observer.disconnect();
          resolve(undefined);
        }
      });

      observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
    });
  }

  /**
   * Shoppers who never want add-ons can fold the block away; the choice is
   * remembered across drawer re-renders and page loads.
   * @returns {boolean}
   */
  get #collapsed() {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  }

  set #collapsed(value) {
    try {
      localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0');
    } catch {
      // Private mode - the block simply won't remember the choice.
    }
  }

  async #load() {
    this.#controller?.abort();

    /** @type {Array<{ id: number, limit: number }>} */
    let targets = [];
    try {
      targets = JSON.parse(this.dataset.targets || '[]');
    } catch {
      targets = [];
    }

    if (!targets.length || !this.dataset.url) {
      this.#render([]);
      return;
    }

    const controller = new AbortController();
    this.#controller = controller;

    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    const intent = this.dataset.intent || 'complementary';
    const base = this.dataset.url;

    try {
      const responses = await Promise.all(
        targets.map((target) =>
          fetch(`${base}?product_id=${target.id}&limit=${target.limit + exclude.size}&intent=${intent}`, {
            signal: controller.signal,
          })
            .then((response) => (response.ok ? response.json() : { products: [] }))
            .catch(() => ({ products: [] }))
        )
      );

      if (controller.signal.aborted) return;

      const seen = new Set(exclude);
      const picked = [];

      responses.forEach((response, index) => {
        const limit = targets[index]?.limit ?? 0;
        let taken = 0;

        for (const product of response.products || []) {
          if (picked.length >= MAX_ITEMS || taken >= limit) break;

          const id = String(product.id);
          if (seen.has(id)) continue;

          seen.add(id);
          picked.push(product);
          taken++;
        }
      });

      this.#render(picked.slice(0, MAX_ITEMS));
    } catch {
      if (!controller.signal.aborted) this.#render([]);
    }
  }

  /** @returns {Intl.NumberFormat} */
  get #formatter() {
    const currency = typeof Shopify !== 'undefined' ? Shopify.currency.active : 'USD';
    const locale = document.documentElement.lang || 'en';
    return new Intl.NumberFormat(locale, { style: 'currency', currency });
  }

  /**
   * @param {Array<Record<string, any>>} products
   */
  #render(products) {
    if (!products.length) {
      this.replaceChildren();
      return;
    }

    const formatter = this.#formatter;

    const header = document.createElement('button');
    header.type = 'button';
    header.className = 'cart-drawer-recommendations__header';
    header.setAttribute('aria-expanded', String(!this.#collapsed));

    const caption = document.createElement('span');
    caption.className = 'cart-drawer-recommendations__caption';
    caption.textContent = this.dataset.caption || '';

    const chevron = document.createElement('span');
    chevron.className = 'cart-drawer-recommendations__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';

    header.append(caption, chevron);
    header.addEventListener('click', () => {
      const next = !this.classList.contains('is-collapsed');
      this.classList.toggle('is-collapsed', next);
      header.setAttribute('aria-expanded', String(!next));
      this.#collapsed = next;
    });

    const list = document.createElement('div');
    list.className = 'cart-drawer-recommendations__list';

    for (const product of products) {
      const variant = product.variants?.find((candidate) => candidate.available) || product.variants?.[0];
      const rawImage = product.featured_image || product.images?.[0];
      const image = typeof rawImage === 'string' ? rawImage : rawImage?.src;
      const price = formatter.format((product.price_min ?? product.price ?? variant?.price ?? 0) / 100);

      const item = document.createElement('div');
      item.className = 'cart-drawer-recommendations__item';

      const mediaWrap = document.createElement('div');
      mediaWrap.className = 'cart-drawer-recommendations__media-wrap';

      const media = document.createElement('a');
      media.className = 'cart-drawer-recommendations__media';
      media.href = product.url;
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = product.title || '';
        img.loading = 'lazy';
        img.width = 120;
        img.height = 120;
        media.append(img);
      }

      const addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'cart-drawer-recommendations__add';
      addButton.setAttribute('aria-label', this.dataset.addText || 'Add to cart');
      addButton.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
      addButton.disabled = !product.available;
      addButton.addEventListener('click', () => this.#handleAdd(product, addButton));

      mediaWrap.append(media, addButton);

      const info = document.createElement('div');
      info.className = 'cart-drawer-recommendations__info';

      const titleLink = document.createElement('a');
      titleLink.href = product.url;
      titleLink.className = 'cart-drawer-recommendations__product-title';
      titleLink.title = product.title || '';
      titleLink.textContent = product.title || '';

      const priceEl = document.createElement('span');
      priceEl.className = 'cart-drawer-recommendations__price';
      priceEl.textContent = price;

      info.append(titleLink, priceEl);

      item.append(mediaWrap, info);
      list.append(item);
    }

    this.classList.toggle('is-collapsed', this.#collapsed);
    this.replaceChildren(header, list);
  }

  /**
   * A product with a single variant is added straight away; anything with a
   * real choice (size, colour) opens a picker instead of silently adding the
   * first variant.
   * @param {Record<string, any>} product
   * @param {HTMLButtonElement} button
   */
  #handleAdd(product, button) {
    const variants = product.variants || [];

    if (variants.length === 1) {
      this.#addToCart(variants[0]?.id, button);
      return;
    }

    this.#openPicker(product);
  }

  /**
   * @param {Record<string, any>} product
   */
  #openPicker(product) {
    this.#picker?.remove();

    const formatter = this.#formatter;
    const options = (product.options || []).map((option) =>
      typeof option === 'string' ? { name: option, values: [] } : option
    );
    const variants = product.variants || [];

    /** @type {Array<string | null>} */
    const selected = (variants.find((variant) => variant.available) || variants[0])?.options?.slice() ?? [];

    // The dialog lives on <body>, outside the drawer, so it has to carry a
    // color-scheme class of its own or none of the theme's colour custom
    // properties resolve.
    const scheme = [...(this.closest('[class*="color-scheme-"]')?.classList ?? [])].find((name) =>
      name.startsWith('color-scheme-')
    );

    const dialog = document.createElement('dialog');
    dialog.className = `cdr-picker ${scheme || 'color-scheme-1'}`;

    const inner = document.createElement('div');
    inner.className = 'cdr-picker__inner';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'cdr-picker__close';
    close.setAttribute('aria-label', this.dataset.closeText || 'Close');
    close.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    close.addEventListener('click', () => dialog.close());

    const head = document.createElement('div');
    head.className = 'cdr-picker__head';

    const rawImage = product.featured_image || product.images?.[0];
    const image = typeof rawImage === 'string' ? rawImage : rawImage?.src;
    if (image) {
      const img = document.createElement('img');
      img.className = 'cdr-picker__image';
      img.src = image;
      img.alt = product.title || '';
      head.append(img);
    }

    const headText = document.createElement('div');
    headText.className = 'cdr-picker__head-text';

    const titleLink = document.createElement('a');
    titleLink.className = 'cdr-picker__title';
    titleLink.href = product.url;
    titleLink.textContent = product.title || '';

    const priceEl = document.createElement('span');
    priceEl.className = 'cdr-picker__price';

    headText.append(titleLink, priceEl);
    head.append(headText);

    const optionsWrap = document.createElement('div');
    optionsWrap.className = 'cdr-picker__options';

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'button cdr-picker__submit';
    submit.textContent = this.dataset.addText || 'Add to cart';

    /**
     * A value stays clickable while some available variant carries it in
     * combination with the other options already picked.
     * @param {number} index
     * @param {string} value
     */
    const valueAvailable = (index, value) =>
      variants.some(
        (variant) =>
          variant.available &&
          variant.options?.[index] === value &&
          selected.every(
            (current, position) => position === index || current == null || variant.options?.[position] === current
          )
      );

    const currentVariant = () =>
      variants.find((variant) => selected.every((current, position) => variant.options?.[position] === current));

    const refresh = () => {
      for (const button of optionsWrap.querySelectorAll('.cdr-picker__value')) {
        if (!(button instanceof HTMLButtonElement)) continue;

        const index = Number(button.dataset.optionIndex);
        const isSelected = button.dataset.value === selected[index];

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        button.classList.toggle('is-unavailable', !valueAvailable(index, button.dataset.value || ''));
      }

      const variant = currentVariant();
      priceEl.textContent = formatter.format((variant?.price ?? product.price ?? 0) / 100);
      submit.disabled = !variant?.available;
      submit.textContent = variant?.available
        ? this.dataset.addText || 'Add to cart'
        : this.dataset.soldOutText || 'Sold out';
    };

    for (const [index, option] of options.entries()) {
      const values = option.values?.length
        ? option.values
        : [...new Set(variants.map((variant) => variant.options?.[index]).filter(Boolean))];

      if (values.length < 2 && values[0] === 'Default Title') continue;

      const group = document.createElement('div');
      group.className = 'cdr-picker__group';

      const label = document.createElement('span');
      label.className = 'cdr-picker__label';
      label.textContent = option.name || '';

      const list = document.createElement('div');
      list.className = 'cdr-picker__values';

      for (const value of values) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cdr-picker__value';
        button.dataset.optionIndex = String(index);
        button.dataset.value = value;
        button.textContent = value;
        button.addEventListener('click', () => {
          selected[index] = value;
          refresh();
        });
        list.append(button);
      }

      group.append(label, list);
      optionsWrap.append(group);
    }

    submit.addEventListener('click', () => {
      const variant = currentVariant();
      if (!variant?.available) return;
      this.#addToCart(variant.id, submit, () => dialog.close());
    });

    inner.append(close, head, optionsWrap, submit);
    dialog.append(inner);

    dialog.addEventListener('close', () => {
      dialog.remove();
      if (this.#picker === dialog) this.#picker = null;
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    document.body.append(dialog);
    this.#picker = dialog;
    refresh();
    dialog.showModal();
  }

  /**
   * @param {number | undefined} variantId
   * @param {HTMLButtonElement} button
   * @param {() => void} [onSuccess]
   */
  async #addToCart(variantId, button, onSuccess) {
    if (!variantId || button.disabled) return;

    button.disabled = true;

    try {
      const sectionId = this.dataset.sectionId || '';
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: sectionId,
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });

      const result = await response.json();

      if (!response.ok) {
        button.disabled = false;
        return;
      }

      onSuccess?.();

      document.dispatchEvent(
        new CartAddEvent(result, this.id || 'cart-drawer-recommendations', {
          source: 'cart-drawer-recommendations',
          variantId: String(variantId),
          sections: result.sections,
        })
      );
    } catch {
      button.disabled = false;
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
